import { brandApi } from '@/api/account-management/brandApi';
import { organizationApi } from '@/api/account-management/organizationApi';
import { type Brand } from '@/models/account-management/Brand';
import { type Location } from '@/models/account-management/Location';
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import TransferList, { type KeyValuePair } from "./TransferList";
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useAppContext } from '../../../AppContext';

export interface LocationTransferList {
    selected: string[] | undefined;
    setSelected: (items: string[]) => void;
    listWidth?: number;
    listHeight?: number;
}

const LocationTransferList = ({ selected = [], setSelected, listWidth, listHeight }: LocationTransferList) => {
    const app = useAppContext();
    const strings = useLocalizedStrings();

    // A user is scoped to the organization, not to one brand, so the picker has to offer every
    // location in the org. There is no org-wide locations endpoint — the locations resource
    // requires a brandId — so fan out over the org's brands. Reusing the ['locations', brandId]
    // key shares the cache with every other locations query in the app.
    const { data: brandList } = useQuery<Brand[]>({
        queryKey: ['brands', app.organizationId],
        enabled: !!app.organizationId,
        queryFn: () => organizationApi.listBrands(app.organizationId!),
        staleTime: Infinity,
    });

    const brands = brandList ?? [];

    const locationQueries = useQueries({
        queries: brands.map(brand => ({
            queryKey: ['locations', brand.id],
            queryFn: () => brandApi.listLocations(brand.id!),
            staleTime: Infinity,
        })),
    });

    const [unselected, setUnselected] = useState<KeyValuePair<string>[]>([]);

    // Flattened in brand order so the group headings read the same way the brand list does.
    const allLocations = brands.flatMap((brand, i) =>
        ((locationQueries[i]?.data ?? []) as Location[]).map(l => ({ location: l, brand }))
    );
    // allLocations is a new array every render, so key the effect on what it actually contains.
    // This also re-syncs after a refetch, which a one-shot "has loaded" flag would miss.
    const signature = allLocations.map(l => l.location.id).join(',');

    useEffect(() => {
        setUnselected(allLocations.map(({ location, brand }) => ({ key: location.id!, value: location.name, group: brand.name })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    const getLeft = () => {
        // Remove the passed in selected items from the unselected list
        return unselected.filter(l => !selected.includes(l.key));
    }

    const getRight = () => {
        // Walk the locations rather than the selection so both columns group in brand order.
        const known: KeyValuePair<string>[] = allLocations
            .filter(l => selected.includes(l.location.id!))
            .map(({ location, brand }) => ({ key: location.id!, value: location.name, group: brand.name }));
        // Anything selected that hasn't loaded yet still needs a row, or it would silently vanish.
        const pending: KeyValuePair<string>[] = selected
            .filter(id => !allLocations.some(l => l.location.id === id))
            .map(id => ({ key: id, value: strings.loading }));
        return [...known, ...pending];
    }

    return (
        <TransferList
            left={getLeft()}
            setLeft={setUnselected}
            right={getRight()}
            setRight={(s) => setSelected(s.map(l => l.key))} // Extract the keys from the selected items
            listWidth={listWidth}
            listHeight={listHeight}
        />
    );
}

export default LocationTransferList;
